import React from 'react';
import styled from '@emotion/styled';
import { Flex } from '@qga/roo-ui/components';

import { useScrollIntoView } from '@shared/v2/components/InView';
import Skeleton from '@shared/v2/components/Skeleton';
import { SCREEN_SIZE } from '@shared/v2/constants';

import Button from '@/components/Button';
import Heading from '@/components/Heading';
import Text from '@/components/Text';
import { MOBILE_ACTIONS } from '@/v2/components/pages/PackageViewV2/components/MainCTA/utils/getCTAButtonVariant/getCTAButtonVariant';
import OfferPrice from '@/v2/components/pages/PackageViewV2/components/OfferPrice';
import { useRoomOptionsData } from '@/v2/components/pages/PackageViewV2/contexts/RoomOptionsContext';
import { useSearchPanelControls } from '@/v2/components/pages/PackageViewV2/contexts/SearchPanelContext';
import { mainCtaDataLayerEvent } from '@/v2/components/pages/PackageViewV2/utils/dataLayer';
import AlternativeOfferCta from './components/AlternativeOfferCta';
import getCTAButtonVariant from './utils/getCTAButtonVariant';
import getIsOccupantsExceeded from './utils/getIsOccupantsExceeded';

const CTAButton = styled(Button)`
  flex: 1 1 0;
  padding-left: 0.75rem;
  padding-right: 0.75rem;

  ${SCREEN_SIZE.LG} {
    max-width: 240px;
  }
`;

const MainCTA = ({
  isMobileVersion,
  handleReserve,
}: {
  isMobileVersion: boolean;
  handleReserve: () => void;
}) => {
  const scrollIntoView = useScrollIntoView();
  const { openCalendar, openMobileSearchPanelModal } = useSearchPanelControls();
  const {
    isPreSearch,
    selectedRoomOption,
    travellerCount,
    isOriginSelected,
    isPackageSelected,
    roomOptions,
    isLoading,
  } = useRoomOptionsData() || {};
  const isOccupantsExceeded = getIsOccupantsExceeded(
    roomOptions?.main,
    travellerCount || 0,
  );
  const isPackageAvailable = !!selectedRoomOption?.matchingOffer;
  const { text, section, mobileAction } = getCTAButtonVariant({
    isPreSearch,
    isOriginSelected,
    isPackageSelected,
    isOccupantsExceeded,
    isPackageAvailable,
  });

  const onClick = () => {
    const shouldInstantScroll = isMobileVersion && mobileAction;
    scrollIntoView(
      section,
      shouldInstantScroll ? undefined : { behavior: 'smooth' },
    );

    if (text) {
      mainCtaDataLayerEvent({ action: text });
    }

    if (isMobileVersion) {
      switch (mobileAction) {
        case MOBILE_ACTIONS.OPEN_CALENDAR:
          openMobileSearchPanelModal();
          openCalendar();
          return;
        case MOBILE_ACTIONS.OPEN_TRAVELLERS:
          openMobileSearchPanelModal();
          return;
        case MOBILE_ACTIONS.RESERVE:
          handleReserve();
          return;
        default:
          return;
      }
    }
  };

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-end"
      width={['100%', null, '342px']}
      pt={['0', null, '1']}
    >
      {!isPreSearch &&
        !isMobileVersion &&
        (isPackageAvailable ? (
          <OfferPrice offer={selectedRoomOption.matchingOffer!} />
        ) : (
          <Skeleton height="48px" width="342px" mb="2">
            <Heading.h5 color="primary" mb="2">
              Unavailable
            </Heading.h5>
            <Text textAlign="right">
              {isOccupantsExceeded
                ? 'This package cannot accommodate the selected number of travellers.'
                : 'This package is not available on the selected travel dates.'}
            </Text>
          </Skeleton>
        ))}
      <Flex gap="2" width="100%" justifyContent="flex-end">
        {isMobileVersion && !selectedRoomOption && !isLoading && (
          <AlternativeOfferCta />
        )}
        <Skeleton height="48px" width={['100%', null, '240px']}>
          <CTAButton
            onClick={onClick}
            width="100%"
            mt={['0', null, '4']}
            nowrap
          >
            {text}
          </CTAButton>
        </Skeleton>
      </Flex>
    </Flex>
  );
};

export default MainCTA;
